import { useEffect, useMemo } from 'react';
import { useBridgeContext } from '@sendible/shared-state-bridge';
import { fetchAllMessageIdsForRange, PAGE_SIZE_DEFAULT, useGetSummaryMessagesByWeek } from '@sendible/frontendv2/src/models/calendar';
import { Container, WeekColumn } from './index.styles';
import { useCalendarContext } from '../../../../context';
import { useSelectionContext } from '../../../../selectionContext';
import { keyFromDate, getWeekRange, startOfWeek } from '../../../../utils';
import { Header, WeekDayColumn } from '..';

interface Props {
  onRender: () => void;
}

interface DayMeta {
  iso: CalendarMonthString;
  date: Date;
}

// Stable fallback reference: `summaryWeek || []` would create a new array on every render
// while the query is loading, which the visible-ids/select-all-capability effects below key
// off of — an unstable dependency there re-fires the effect (and its setState) every render,
// causing an infinite update loop instead of just a one-off recompute.
const EMPTY_SUMMARY: CalendarData[] = [];

export const WeeklyGrid = ({ onRender }: Props) => {
  const [{ user }] = useBridgeContext();
  const { currentDate, todayDate, userWeekStartDay, filterParams } = useCalendarContext();
  const { clearVisibleMessageIds, setSelectAllCapability, setVisibleMessageIds } = useSelectionContext();
  const { data: summaryWeek } = useGetSummaryMessagesByWeek(currentDate, userWeekStartDay);
  const summaryList = summaryWeek || EMPTY_SUMMARY;

  const summaryByDate = useMemo(() => {
    const map: Record<string, CalendarData> = {};

    summaryList.forEach((s) => {
      map[s.date] = s;
    });

    return map;
  }, [summaryList]);

  const columns: DayMeta[] = useMemo(() => {
    const start = startOfWeek(currentDate, userWeekStartDay);
    const today = new Date();

    today.setHours(0, 0, 0, 0);

    return Array.from({ length: 7 }, (_, i) => {
      const dt = new Date(start);

      dt.setDate(start.getDate() + i);

      const iso = keyFromDate(dt) as CalendarMonthString;

      return { iso, date: dt };
    });
  }, [currentDate, userWeekStartDay]);

  useEffect(() => onRender(), []);

  useEffect(() => {
    setVisibleMessageIds(
      'weekly-grid',
      summaryList.flatMap((day) => (day.messages || []).map((message) => message.id))
    );
  }, [summaryList, setVisibleMessageIds]);

  useEffect(() => () => clearVisibleMessageIds('weekly-grid'), [clearVisibleMessageIds]);

  // "Select all" for the weekly view means every post in the visible week, not just the ones
  // already loaded per day column (each column infinite-scrolls in extra pages once a day has
  // more posts than fit initially, see PAGE_SIZE_DEFAULT) — fetch the full week range on demand
  // rather than only ever selecting what happened to already be loaded/scrolled into view.
  useEffect(() => {
    const totalCount = summaryList.reduce((sum, day) => sum + (day.messageCount ?? 0), 0);

    setSelectAllCapability({
      totalCount,
      fetchAllIds: async (signal) => {
        const [startISO, endISO] = getWeekRange(currentDate, userWeekStartDay);

        return fetchAllMessageIdsForRange({
          accessToken: user?.accessToken ?? '',
          userId: user?.userId ?? 0,
          email: user?.email ?? '',
          endISO,
          filterParams,
          isSwitchedUser: user?.isSwitchedUser,
          isWorkspaceMode: Boolean(user?.isWorkspaceMode),
          signal,
          startISO,
          username: user?.username,
        });
      },
    });

    return () => setSelectAllCapability(null);
  }, [summaryList, currentDate, userWeekStartDay, filterParams, user, setSelectAllCapability]);

  return (
    <>
      <Header
        showWeekNumbers
        userWeekStartDay={userWeekStartDay}
      />
      <Container className="weekly-grid">
        {columns.map((col, index) => {
          const isPast = col.iso < todayDate;
          const isToday = col.iso === todayDate;
          const summary = summaryByDate[col.iso];

          return (
            <WeekColumn
              key={col.iso}
              className="week-day-column"
              data-testid={`week-day-column-${index}`}
            >
              <WeekDayColumn
                iso={col.iso}
                isPast={isPast}
                isToday={isToday}
                summary={summary}
                pageSize={PAGE_SIZE_DEFAULT}
              />
            </WeekColumn>
          );
        })}
      </Container>
    </>
  );
};
